import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

const Terms = () => {
  const { toast } = useToast();

  const handleDownloadPDF = async () => {
    toast({
      title: "Generating PDF...",
      description: "Please wait while we prepare your document.",
    });

    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let yPosition = 20;

    const addText = (text: string, fontSize: number = 10, isBold: boolean = false) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      const lines = doc.splitTextToSize(text, maxWidth);
      
      lines.forEach((line: string) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, margin, yPosition);
        yPosition += fontSize * 0.5;
      });
      yPosition += 3;
    };

    const addSection = (title: string, content: string[]) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      addText(title, 12, true);
      yPosition += 2;
      content.forEach(text => addText(text, 10));
      yPosition += 5;
    };

    // Title
    addText('OdyVoy Terms of Use', 18, true);
    addText('Last updated: April 9, 2026', 10);
    yPosition += 10;

    addSection('1. Agreement to Terms', [
      'By accessing or using OdyVoy ("the Service"), you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use the Service. OdyVoy is operated by a sole proprietor registered in Greece under Greek law.'
    ]);

    addSection('2. Description of Service', [
      'OdyVoy is a travel planning and packing assistant application that provides:',
      '• Personalized packing list recommendations based on destination, weather, and trip type',
      '• Weather forecasts and travel insights for destinations worldwide',
      '• Visa requirement information and travel documentation guidance',
      '• Budget tracking and expense management tools',
      '• Health and wellness travel tips',
      '• Local tips and cultural information for destinations',
      '• AI-powered travel assistant and support chat',
      '• Currency converter with bill scanning'
    ]);

    addSection('3. User Accounts', [
      'To access certain features, you must create an account. You are responsible for:',
      '• Maintaining the confidentiality of your account credentials',
      '• All activities that occur under your account',
      '• Providing accurate and complete registration information',
      '• Notifying us immediately of any unauthorized use of your account',
      '',
      'You must be at least 16 years old to create an account and use the Service.'
    ]);

    addSection('4. Subscription Plans and Payments', [
      'Overview of Subscription Tiers:',
      'OdyVoy offers free and paid subscription tiers, as well as temporary Boost Passes and Gift Subscriptions. All paid subscriptions are billed monthly through Stripe. By subscribing, you authorize us to charge your payment method on a recurring basis until you cancel. Subscription fees are non-refundable except where required by applicable law.',
      '',
      'Economy Class Pack (Free Tier):',
      '• Basic Packing Lists with up to 3 uses per day',
      '• Visa Requirements Check with up to 3 uses per day',
      '• Jet Lag Minimizer with up to 3 uses per day',
      '',
      'Premium Economy Class Pack (€7.99/month or €79.9/year):',
      '• Unlimited searches and access to all available tools within the app',
      '• AI-powered personalized packing lists and travel recommendations',
      '• Visa Requirements Check without daily limits',
      '• Local destination tips and cultural guidance',
      '• Health and wellness preparation tools',
      '• Jet Lag Minimizer (unlimited)',
      '• Smart currency converter',
      '• Group expense sharing',
      '• Early access to new features released on the Service',
      '',
      'First Class Pack (€12.99/month or €129.9/year):',
      'Everything in Premium Economy plus:',
      '• Offline mode to access saved lists without an internet connection',
      '• Multi-city packing lists',
      '• Custom travel templates with the ability to save and reuse lists',
      '• Advanced travel analytics, including spending and travel insights',
      '• Group packing progress sharing',
      '',
      'Boost Passes (Temporary Access):',
      'Premium Economy Access: 3-Day €4.49, 7-Day €5.49, 14-Day €6.49',
      'First Class Access: 3-Day €8.49, 7-Day €9.49, 14-Day €10.49',
      'Boost Passes are one-time purchases, do not renew automatically, and access ends at expiry. Unused days cannot be paused or transferred, and no refunds or partial refunds are provided once activated, except where required by applicable law.',
      '',
      'Gift Subscriptions:',
      'Users may purchase prepaid gift subscriptions for Premium Economy or First Class plans. Gift Subscriptions are paid upfront for the entire selected term and are not renewed automatically.',
      '',
      'Redemption Window: Recipients must redeem their gift subscription within a specified timeframe: 1-month gifts within 14 days, 3-month gifts within 20 days, 6-month gifts within 60 days, and 12-month gifts within 90 days. Gift codes not redeemed by the deadline become inaccessible.',
      '',
      'Premium Economy Gift: 1-Month €7.99, 3-Month €22.99, 6-Month €43.99, 12-Month €79.90',
      'First Class Gift: 1-Month €12.99, 3-Month €37.99, 6-Month €72.99, 12-Month €129.90',
      '',
      'Pricing and Taxes:',
      'All prices are in Euros (EUR) and may be subject to VAT or other applicable taxes according to Greek and EU law. Prices may change over time, and any material changes will be communicated through the Service or via email.'
    ]);

    addSection('5. Referral Program', [
      'OdyVoy offers a referral program that allows users to earn rewards by inviting friends and family.',
      '',
      'How it works:',
      '1. Share Your Referral Link: Each user receives a unique referral link.',
      '2. Friend Receives Discount: When a friend signs up and upgrades, they receive 10% off their first payment.',
      '3. You Earn Free Service: You receive 1 month of free service at your current tier (or Premium Economy if on free tier).',
      '4. Unlimited Referrals: No limit on referrals or accumulated rewards.',
      '',
      'Key Terms:',
      '• Both referrer and referred must have active accounts.',
      '• You may not refer yourself or use multiple accounts for false referrals.',
      '• Rewards are non-transferable and cannot be exchanged for cash.',
      '• OdyVoy reserves the right to disable rewards for fraudulent patterns.',
      '• Program may be modified or discontinued at any time with notice.'
    ]);

    addSection('6. Cancellation Policy', [
      'You may cancel your subscription at any time through your account settings. Upon cancellation:',
      '• You will retain access to paid features until the end of your current billing period',
      '• Your account will be downgraded to the free tier after the billing period ends',
      '• No partial refunds are provided for unused portions of a subscription period'
    ]);

    addSection('7. User Content and Data', [
      'You retain ownership of any content you create within the Service (trip plans, packing lists, expense records, etc.). By using the Service, you grant OdyVoy a limited license to store, process, and display your content solely for the purpose of providing the Service to you.',
      '',
      'You are responsible for ensuring that any content you upload does not violate the rights of third parties or applicable laws.'
    ]);

    addSection('8. Prohibited Uses', [
      'You agree not to:',
      '• Use the Service for any unlawful purpose',
      '• Attempt to gain unauthorized access to any part of the Service',
      '• Interfere with or disrupt the Service or servers',
      '• Use automated systems to access the Service without permission',
      '• Impersonate any person or entity',
      '• Share your account credentials with others',
      '• Use the Service to transmit harmful code or malware'
    ]);

    addSection('9. Intellectual Property', [
      'The Service, including its design, features, content, and underlying technology, is owned by OdyVoy and protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works based on the Service without prior written consent.',
      '',
      'The OdyVoy name, logo, and all related marks are trademarks of OdyVoy.'
    ]);

    addSection('10. AI-Generated Content', [
      '• OdyVoy uses artificial intelligence to generate packing suggestions, travel tips, chat responses, and other recommendations',
      '• AI-generated content is provided for informational purposes only and may contain inaccuracies',
      '• You are responsible for verifying all AI-generated information before relying on it',
      '• We do not guarantee the accuracy, completeness, or suitability of AI-generated content',
      '• AI features may use third-party AI services; your queries are processed but not used to train AI models'
    ]);

    addSection('11. Data Export and Portability', [
      '• You can export your data at any time through the Data Management section in app settings',
      '• Exported data is provided in JSON format for portability',
      '• This includes your trips, packing lists, expenses, and profile information'
    ]);

    addSection('12. Third-Party Services', [
      'The Service may integrate or suggest third-party services, including payment processors (e.g., Stripe), mapping providers, and eSIM companies. Use of these third-party services is subject to their own terms and privacy policies.',
      '',
      'Your decision to use any suggested third-party service is at your own risk. We accept no responsibility or liability for their content, practices, reliability, or availability.'
    ]);

    addSection('13. Disclaimer of Warranties', [
      'The Service is provided "as is" and "as available" without warranties of any kind. While we strive to provide accurate information:',
      '• Weather forecasts are estimates and may not be accurate',
      '• OdyVoy aims to update visa and travel information regularly and will display the last update date. However, this information may change without notice - always verify with official government sources before travel.',
      '• Packing recommendations are suggestions and may not cover all needs',
      '• We do not guarantee uninterrupted or error-free service',
      '',
      'The user cannot be exempted from the responsibility to verify all travel-related information from the official websites before the trip. Any damage caused due to false or inadequate information is attributed to the user.'
    ]);

    addSection('14. Limitation of Liability', [
      'To the maximum extent permitted by Greek and EU law, OdyVoy shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service. Our total liability shall not exceed the amount you paid for the Service in the twelve (12) months preceding the claim.'
    ]);

    addSection('15. Termination', [
      'We may suspend or terminate your access to the Service at any time for violation of these Terms or for serious reason, such as those mentioned under Chapter 8 (Prohibited Uses). Upon termination:',
      '• Your right to use the Service will immediately cease',
      '• You may request a copy of your data before account deletion',
      '• We may delete your account and associated data after a reasonable notice period'
    ]);

    addSection('16. Changes to Terms', [
      'We may update these Terms from time to time. We will notify you of material changes by email or through the Service. Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms.'
    ]);

    addSection('17. Governing Law and Dispute Resolution', [
      'These Terms are governed by the laws of Greece. Any disputes arising from these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of Athens, Greece.'
    ]);

    addSection('18. Severability', [
      'If any provision of Terms is held by a court or other tribunal of competent jurisdiction to be invalid, illegal or unenforceable for any reason, such provision shall be eliminated or limited to the minimum extent such that the remaining provisions of Terms will continue in full force and effect.'
    ]);

    addSection('19. Contact Information', [
      'Business Name: Eirini Maria Oikonomou (Sole Proprietor)',
      'Business Location: Konitsis 39, Vrilissia, 15238, Greece',
      'Email: info@odyvoy.com'
    ]);

    doc.save('OdyVoy-Terms-of-Use.pdf');

    toast({
      title: "PDF Downloaded",
      description: "Terms of Use has been saved to your device.",
    });
  };
  return (
    <>
      <Helmet>
        <title>Terms of Use | OdyVoy - Smart Travel Planning</title>
        <meta name="description" content="OdyVoy Terms of Use - Read our terms and conditions for using our travel planning and packing assistant services." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://odyvoy.com/terms" />
        <meta property="og:title" content="OdyVoy Terms of Use — Service Agreement & Conditions" />
        <meta property="og:description" content="The terms and conditions for using OdyVoy's AI travel planning and smart packing services." />
        <meta property="og:url" content="https://odyvoy.com/terms" />
        <meta property="og:type" content="article" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <Link to="/">
              <Button variant="ghost">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
            <Button variant="outline" onClick={handleDownloadPDF} className="min-h-[44px]">
              <Download className="mr-2 h-4 w-4" />
              Download PDF
            </Button>
          </div>
          
          <h1 className="text-4xl font-bold text-foreground mb-2">OdyVoy Terms of Use</h1>
          <p className="text-muted-foreground text-lg mb-8">
            Last updated: April 9, 2026
          </p>
          
          <div className="prose prose-lg dark:prose-invert max-w-none space-y-6">
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">1. Agreement to Terms</h2>
              <p className="text-muted-foreground">
                By accessing or using OdyVoy ("the Service"), you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use the Service. OdyVoy is operated by a sole proprietor registered in Greece under Greek law.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">2. Description of Service</h2>
              <p className="text-muted-foreground">
                OdyVoy is a travel planning and packing assistant application that provides:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Personalized packing list recommendations based on destination, weather, and trip type</li>
                <li>Weather forecasts and travel insights for destinations worldwide</li>
                <li>Visa requirement information and travel documentation guidance</li>
                <li>Budget tracking and expense management tools</li>
                <li>Health and wellness travel tips</li>
                <li>Local tips and cultural information for destinations</li>
                <li>AI-powered travel assistant and support chat</li>
                <li>Currency converter with bill scanning</li>
              </ul>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">3. User Accounts</h2>
              <p className="text-muted-foreground">
                To access certain features, you must create an account. You are responsible for:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Providing accurate and complete registration information</li>
                <li>Notifying us immediately of any unauthorized use of your account</li>
              </ul>
              <p className="text-muted-foreground">
                You must be at least 16 years old to create an account and use the Service.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">4. Subscription Plans and Payments</h2>
              
              <h3 className="text-xl font-medium text-foreground mt-6">Overview of Subscription Tiers</h3>
              <p className="text-muted-foreground">
                OdyVoy offers free and paid subscription tiers, as well as temporary Boost Passes and Gift Subscriptions. All paid subscriptions are billed monthly through Stripe. By subscribing, you authorize us to charge your payment method on a recurring basis until you cancel. Subscription fees are non-refundable except where required by applicable law.
              </p>

              <h3 className="text-xl font-medium text-foreground mt-6">Economy Class Pack (Free Tier)</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Basic Packing Lists with up to 3 uses per day</li>
                <li>Visa Requirements Check with up to 3 uses per day</li>
                <li>Jet Lag Minimizer with up to 3 uses per day</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mt-6">Premium Economy Class Pack (€6.99/month or €69.90/year)</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Unlimited searches and access to all available tools within the app</li>
                <li>AI-powered personalized packing lists and travel recommendations</li>
                <li>Visa Requirements Check without daily limits</li>
                <li>Local destination tips and cultural guidance</li>
                <li>Health and wellness preparation tools</li>
                <li>Jet Lag Minimizer (unlimited)</li>
                <li>Smart currency converter</li>
                <li>Group expense sharing</li>
                <li>Early access to new features released on the Service</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mt-6">First Class Pack (€11.99/month or €119.90/year)</h3>
              <p className="text-muted-foreground">
                Everything in Premium Economy plus:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Offline mode to access saved lists without an internet connection</li>
                <li>Multi-city packing lists</li>
                <li>Custom travel templates with the ability to save and reuse lists</li>
                <li>Advanced travel analytics, including spending and travel insights</li>
                <li>Group packing progress sharing</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mt-6">Boost Passes (Temporary Access)</h3>
              <p className="text-muted-foreground">
                Users may purchase time-limited "Boost Passes" that unlock Premium Economy or First Class features for a fixed duration. Boost Passes are one-time purchases, are billed in full at the time of purchase, and do not renew automatically. Access granted through a Boost Pass will automatically end at the expiry of the selected duration. Unused days cannot be paused or transferred, and no refunds or partial refunds are provided once a Boost Pass is activated, except where required by applicable law.
              </p>
              <p className="text-muted-foreground font-medium mt-4">Premium Economy Access Boost Passes:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>3-Day Pass: €3.49</li>
                <li>7-Day Pass: €5.99</li>
                <li>14-Day Pass: €9.99</li>
              </ul>
              <p className="text-muted-foreground font-medium mt-4">First Class Access Boost Passes:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>3-Day Pass: €5.99</li>
                <li>7-Day Pass: €10.99</li>
                <li>14-Day Pass: €16.99</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mt-6">Gift Subscriptions</h3>
              <p className="text-muted-foreground">
                Users may purchase prepaid gift subscriptions for Premium Economy or First Class plans to be redeemed by another individual. Gift Subscriptions are paid upfront for the entire selected term and are not renewed automatically. Upon purchase, the purchaser will receive a gift code or link that can be shared with the recipient; OdyVoy is not responsible for lost, misplaced, or improperly shared codes.
              </p>
              <p className="text-muted-foreground mt-4">
                <strong>Redemption Window:</strong> Recipients must redeem their gift subscription within a specified timeframe: 1-month gifts within 14 days, 3-month gifts within 20 days, 6-month gifts within 60 days, and 12-month gifts within 90 days. Gift codes not redeemed by the deadline become inaccessible.
              </p>
              <p className="text-muted-foreground mt-4">
                At the end of the gifted term, the recipient's account will revert to the free Economy tier unless they choose a separate paid subscription. Gift Subscriptions are non-refundable and may not be exchanged for cash or other products, except where mandatory consumer protection laws provide otherwise.
              </p>
              <p className="text-muted-foreground font-medium mt-4">Premium Economy Class Pack Gift:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>1-Month Gift: €6.99</li>
                <li>3-Month Gift: €19.50</li>
                <li>6-Month Gift: €36.49</li>
                <li>12-Month Gift: €67.10</li>
              </ul>
              <p className="text-muted-foreground font-medium mt-4">First Class Pack Gift:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>1-Month Gift: €11.99</li>
                <li>3-Month Gift: €33.45</li>
                <li>6-Month Gift: €62.59</li>
                <li>12-Month Gift: €115.10</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mt-6">Pricing and Taxes</h3>
              <p className="text-muted-foreground">
                All prices displayed are in Euros (EUR) and may be subject to value-added tax (VAT) or other applicable taxes according to Greek and EU law. Prices may change over time, and any material changes will be communicated to you through the Service or via email. Your continued use of the Service after such notification constitutes acceptance of the new prices.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">5. Referral Program</h2>
              <p className="text-muted-foreground">
                OdyVoy offers a referral program that allows users to earn rewards by inviting friends and family to use the Service.
              </p>
              
              <h3 className="text-xl font-medium text-foreground mt-6">How the Referral Program Works</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Share Your Referral Link:</strong> Each user receives a unique referral link that can be shared with friends and family.</li>
                <li><strong>Friend Receives Discount:</strong> When a friend signs up using your referral link and upgrades, they receive a 10% discount on their first subscription payment.</li>
                <li><strong>You Earn Free Service Time:</strong> Once your referred friend completes their subscription, you automatically receive 1 month of free service added to your account at your current subscription tier (or Premium Economy if you are on the free tier).</li>
                <li><strong>Unlimited Referrals:</strong> You may refer as many friends and family members as you wish and accumulate rewards with no limit.</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mt-6">Key Terms</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Eligibility:</strong> Both the referrer and the referred user must have active OdyVoy accounts. You may not refer yourself or use multiple accounts to generate false referrals.</li>
                <li><strong>Reward Timing:</strong> The 1-month free service is automatically applied immediately upon the referred friend's subscription becoming active.</li>
                <li><strong>Stacking Rewards:</strong> Multiple referral rewards can accumulate and be applied sequentially.</li>
                <li><strong>Fraud Prevention:</strong> OdyVoy reserves the right to disable referral rewards if we detect fraudulent, abusive, or manipulative referral patterns.</li>
                <li><strong>Non-Transferable:</strong> Referral rewards are personal to your account and may not be transferred, sold, or exchanged for cash.</li>
                <li><strong>Program Modification:</strong> OdyVoy may modify, suspend, or discontinue the referral program at any time with notice.</li>
              </ul>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">6. Cancellation Policy</h2>
              <p className="text-muted-foreground">
                You may cancel your subscription at any time through your account settings. Upon cancellation:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>You will retain access to paid features until the end of your current billing period</li>
                <li>Your account will be downgraded to the free tier after the billing period ends</li>
                <li>No partial refunds are provided for unused portions of a subscription period</li>
              </ul>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">7. User Content and Data</h2>
              <p className="text-muted-foreground">
                You retain ownership of any content you create within the Service (trip plans, packing lists, expense records, etc.). By using the Service, you grant OdyVoy a limited license to store, process, and display your content solely for the purpose of providing the Service to you.
              </p>
              <p className="text-muted-foreground">
                You are responsible for ensuring that any content you upload does not violate the rights of third parties or applicable laws.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">8. Prohibited Uses</h2>
              <p className="text-muted-foreground">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Use the Service for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to any part of the Service</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Use automated systems to access the Service without permission</li>
                <li>Impersonate any person or entity</li>
                <li>Share your account credentials with others</li>
                <li>Use the Service to transmit harmful code or malware</li>
              </ul>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">9. Intellectual Property</h2>
              <p className="text-muted-foreground">
                The Service, including its design, features, content, and underlying technology, is owned by OdyVoy and protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works based on the Service without prior written consent.
              </p>
              <p className="text-muted-foreground">
                The OdyVoy name, logo, and all related marks are trademarks of OdyVoy.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">10. AI-Generated Content</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>OdyVoy uses artificial intelligence to generate packing suggestions, travel tips, chat responses, and other recommendations</li>
                <li>AI-generated content is provided for informational purposes only and may contain inaccuracies</li>
                <li>You are responsible for verifying all AI-generated information before relying on it</li>
                <li>We do not guarantee the accuracy, completeness, or suitability of AI-generated content</li>
                <li>AI features may use third-party AI services; your queries are processed but not used to train AI models</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">11. Data Export and Portability</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>You can export your data at any time through the Data Management section in app settings</li>
                <li>Exported data is provided in JSON format for portability</li>
                <li>This includes your trips, packing lists, expenses, and profile information</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">12. Third-Party Services</h2>
              <p className="text-muted-foreground">
                The Service may integrate or suggest third-party services, including payment processors (e.g., <a href="https://stripe.com/en-gr/legal/ssa" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">Stripe</a>), mapping providers, and eSIM companies for connectivity solutions. Use of these third-party services is subject to their own terms, conditions, and privacy policies, which we do not control.
              </p>
              <p className="text-muted-foreground">
                Your decision to use, purchase, or interact with any suggested third-party service (including third-party eSIM providers) is at your own risk. We accept no responsibility or liability for their content, practices, reliability, or availability.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">13. Disclaimer of Warranties</h2>
              <p className="text-muted-foreground">
                The Service is provided "as is" and "as available" without warranties of any kind. While we strive to provide accurate information:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Weather forecasts are estimates and may not be accurate</li>
                <li>OdyVoy aims to update visa and travel information regularly and will display the last update date. However, this information may change without notice - always verify with official government sources before travel.</li>
                <li>Packing recommendations are suggestions and may not cover all needs</li>
                <li>We do not guarantee uninterrupted or error-free service</li>
              </ul>
              <p className="text-muted-foreground">
                The user cannot be exempted from the responsibility to verify all travel-related information from the official websites before the trip. Any damage caused due to false or inadequate information is attributed to the user.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">14. Limitation of Liability</h2>
              <p className="text-muted-foreground">
                To the maximum extent permitted by Greek and EU law, OdyVoy shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service. Our total liability shall not exceed the amount you paid for the Service in the twelve (12) months preceding the claim.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">15. Termination</h2>
              <p className="text-muted-foreground">
                We may suspend or terminate your access to the Service at any time for violation of these Terms or for serious reason, such as those mentioned under Chapter 8 (Prohibited Uses). Upon termination:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Your right to use the Service will immediately cease</li>
                <li>You may request a copy of your data before account deletion</li>
                <li>We may delete your account and associated data after a reasonable notice period</li>
              </ul>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">16. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We may update these Terms from time to time. We will notify you of material changes by email or through the Service. Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">17. Governing Law and Dispute Resolution</h2>
              <p className="text-muted-foreground">
                These Terms are governed by the laws of Greece. Any disputes arising from these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of Athens, Greece.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">18. Severability</h2>
              <p className="text-muted-foreground">
                If any provision of Terms is held by a court or other tribunal of competent jurisdiction to be invalid, illegal or unenforceable for any reason, such provision shall be eliminated or limited to the minimum extent such that the remaining provisions of Terms will continue in full force and effect.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">19. Contact Information</h2>
              <p className="text-muted-foreground">
                <strong>Business Name:</strong> Eirini Maria Oikonomou (Sole Proprietor)<br />
                <strong>Business Location:</strong> Konitsis 39, Vrilissia, 15238, Greece<br />
                <strong>Email:</strong>{" "}
                <a href="mailto:info@odyvoy.com" className="text-primary hover:underline">info@odyvoy.com</a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;
